import "./PriceRuleComponent.scss";
import {IComponentOptions, IOnChanges, IOnChangesObject, IOnDestroy, IOnInit} from "angular";
import {AlertDialogPriceRule} from "../../models/AlertDialogPriceRule";
import {PriceAlertType} from "./models/PriceAlertType";
import {ReplaySubject} from "rxjs";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {ObjectUtil} from "../../../../../js/util/ObjectUtil";
import {UnderlyingStatistics} from "../../../../models/UnderlyingStatistics";
import {RuleComponent} from "../RuleComponent";

export const PriceRule = "tfPriceAlertRule";

export function priceRule(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/price/PriceRuleComponent.html`,
        require: {
            ruleList: "^^tfAlertRuleList"
        },
        bindings: {
            rule: "<",
            statistics: "<",
            onChange: "&"
        },
        controllerAs: "ctrl",
        controller: PriceRuleComponentController
    };
}

export class PriceRuleComponentController extends RuleComponent implements IOnInit, IOnChanges, IOnDestroy {

    public readonly rule: AlertDialogPriceRule;
    public readonly statistics: UnderlyingStatistics;
    public readonly onChange: ComponentCallback;

    public type = PriceAlertType.ABOVE;
    public priceAbove: number | null = null;
    public priceBelow: number | null = null;
    public priceMove: number | null = null;
    public priceLowerThanCurrent = false;
    public priceHigherThanCurrent = false;

    private readonly changed$ = new ReplaySubject<boolean>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public $onInit(): void {
        super.$onInit();
        this.changed$
            .takeUntil(this.destroyed$)
            .debounceTime(200)
            .map(() => this.buildRule())
            .subscribe((rule) => this.onChange({$rule: rule}));
    }

    private buildRule(): AlertDialogPriceRule {
        const copy = ObjectUtil.copy(this.rule);
        copy.priceType = this.type;
        copy.priceAbove = this.priceAbove;
        copy.priceBelow = this.priceBelow;
        copy.pricePercentMove = this.priceMove;
        return copy;
    }

    public $onChanges(changes: IOnChangesObject): void {
        if (changes.rule != null && this.rule != null) {
            this.type = this.currentPriceType(this.rule);
            this.updateCurrentPrice(this.rule);
        }
    }

    private currentPriceType(rule: AlertDialogPriceRule): PriceAlertType {
        if (rule.priceType != null) {
            return rule.priceType;
        } else if (rule.priceAbove != null) {
            return PriceAlertType.ABOVE;
        } else if (rule.priceBelow != null) {
            return PriceAlertType.BELOW;
        } else if (rule.pricePercentMove != null) {
            return PriceAlertType.MOVE;
        } else {
            return PriceAlertType.ABOVE;
        }
    }

    private updateCurrentPrice(rule: AlertDialogPriceRule) {
        this.priceAbove = rule.priceAbove;
        this.priceBelow = rule.priceBelow;
        this.priceMove = rule.pricePercentMove;
    }

    public changeType(type: PriceAlertType) {
        this.type = type;
        this.ruleChanged();
    }

    public ruleChanged() {
        this.changed$.next(true);
        this.priceHigherThanCurrent = this.isHigherThanCurrentPrice();
        this.priceLowerThanCurrent = this.isLowerThanCurrentPrice();
    }

    public get wouldTriggerImmediately(): boolean {
        return this.isHigherThanCurrentPrice() || this.isLowerThanCurrentPrice();
    }

    private isLowerThanCurrentPrice(): boolean {
        const currentPrice = this.statistics.currentPrice;
        if (this.type === PriceAlertType.ABOVE &&
            this.priceAbove! < currentPrice) {
            return true;
        }

        return false;
    }

    private isHigherThanCurrentPrice(): boolean {
        const currentPrice = this.statistics.currentPrice;
        if (this.type === PriceAlertType.BELOW &&
            this.priceBelow! > currentPrice) {
            return true;
        }

        return false;
    }

    public $onDestroy(): void {
        super.$onDestroy();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
